import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { DollarSign, BookOpen, Star, AlertCircle, Clock, Activity, Calendar, MapPin } from 'lucide-react';
import { format, subMonths, startOfMonth, endOfMonth, isAfter, isBefore, addDays } from 'date-fns';
import useClients from '../hooks/useClients';
import useBookings from '../hooks/useBookings';
import usePayments from '../hooks/usePayments';
import useDashboardStats from '../hooks/useDashboardStats';
import useActivityLog from '../hooks/useActivityLog';
import WeatherAlertBanner from '../components/WeatherAlertBanner';

function KPICard({ icon: Icon, label, value, sub, color = '#C9A84C' }) {
  return (
    <div className="bg-white rounded-2xl p-5 shadow-sm border-l-4" style={{ borderLeftColor: color }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-xs font-medium uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
        </div>
        <div className="p-2 rounded-lg" style={{ backgroundColor: color + '20' }}>
          <Icon size={20} style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// Build 6-month revenue chart from bookings (authoritative source)
function buildChartData(bookings) {
  const months = Array.from({ length: 6 }, (_, i) => {
    const d = subMonths(new Date(), 5 - i);
    return {
      month: format(d, 'MMM'),
      start: startOfMonth(d),
      end: endOfMonth(d),
      maintenance: 0,
      weekend: 0,
    };
  });

  bookings.forEach(b => {
    if (b.status !== 'completed') return;
    const raw = b.scheduled_at || b.created_at;
    if (!raw) return;
    const d = raw.toDate ? raw.toDate() : new Date(raw);
    const bucket = months.find(m => isAfter(d, m.start) && isBefore(d, m.end));
    if (!bucket) return;
    if (b.booking_type === 'maintenance') bucket.maintenance += Number(b.price) || 0;
    else bucket.weekend += Number(b.price) || 0;
  });

  return months.map(m => ({ month: m.month, Maintenance: m.maintenance, Weekend: m.weekend }));
}

function JobAddressList({ bookings }) {
  const upcoming = useMemo(() => {
    const now = new Date();
    const cutoff = addDays(now, 14);
    return bookings.filter(b => {
      if (!b.scheduled_at) return false;
      if (b.status === 'cancelled' || b.status === 'completed') return false;
      const d = new Date(b.scheduled_at);
      return d >= now && d <= cutoff;
    });
  }, [bookings]);

  if (!upcoming.length) {
    return <p className="text-gray-400 text-sm">No upcoming jobs in the next 14 days.</p>;
  }

  return (
    <ul className="divide-y divide-gray-100">
      {upcoming.map(b => {
        const t = b.scheduled_at?.toDate ? b.scheduled_at.toDate() : new Date(b.scheduled_at);
        return (
          <li key={b.id} className="py-3 flex items-start justify-between gap-4 text-sm">
            <div className="flex items-start gap-3">
              <MapPin size={14} className="text-[#68caf1] mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-gray-800">{b.client_name}</p>
                {b.address && <p className="text-gray-500 text-xs mt-0.5">{b.address}</p>}
                <p className="text-gray-400 text-xs">{b.tier || b.service_type}</p>
              </div>
            </div>
            <span className="text-xs text-gray-500 whitespace-nowrap">{format(t, 'EEE, MMM d')}</span>
          </li>
        );
      })}
    </ul>
  );
}

export default function Dashboard() {
  const { clients, loading: cLoading } = useClients();
  const { bookings, loading: bLoading } = useBookings();
  const { payments, loading: pLoading } = usePayments();
  const { activity } = useActivityLog(10);

  const stats = useDashboardStats({ clients, bookings, payments });
  const chartData = buildChartData(bookings);
  const loading = cLoading || bLoading || pLoading;

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A84C]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 pb-24 md:pb-6">
      <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>

      {/* Weather alerts */}
      <WeatherAlertBanner bookings={bookings} />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          icon={DollarSign}
          label="Revenue MTD"
          value={`$${stats.mtdRevenue.toLocaleString()}`}
          sub="from bookings this month"
          color="#C9A84C"
        />
        <KPICard
          icon={BookOpen}
          label="Jobs This Week"
          value={stats.weekBookings}
          sub="Mon – Sun"
          color="#68caf1"
        />
        <KPICard
          icon={Star}
          label="Maintenance Slots"
          value={`${stats.maintenanceSlots}/20`}
          sub={`${20 - stats.maintenanceSlots} open`}
          color="#10b981"
        />
        <KPICard
          icon={AlertCircle}
          label="Outstanding Balance"
          value={`$${stats.outstanding.toLocaleString()}`}
          sub="pending manual payments"
          color="#f59e0b"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue chart */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4">Revenue — Last 6 Months</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v}`} />
              <Tooltip formatter={v => `$${v}`} />
              <Legend />
              <Bar dataKey="Maintenance" fill="#C9A84C" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Weekend" fill="#68caf1" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Today's schedule */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Clock size={16} className="text-[#C9A84C]" /> Today's Schedule
          </h3>
          {stats.todayJobs.length === 0 ? (
            <div className="space-y-3">
              <p className="text-gray-400 text-sm">No jobs today.</p>
              {stats.upcomingJobs.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Up Next</p>
                  <ul className="space-y-2">
                    {stats.upcomingJobs.slice(0, 3).map(j => {
                      const t = j.scheduled_at?.toDate ? j.scheduled_at.toDate() : new Date(j.scheduled_at);
                      return (
                        <li key={j.id} className="flex items-start gap-2 text-sm">
                          <div>
                            <p className="font-medium text-gray-800">{j.client_name}</p>
                            <p className="text-gray-400 text-xs">{format(t, 'EEE, MMM d · h:mm a')}</p>
                            <p className="text-gray-500 text-xs">{j.tier || j.service_type}</p>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <ul className="space-y-2">
              {stats.todayJobs.map(j => {
                const t = j.scheduled_at?.toDate ? j.scheduled_at.toDate() : new Date(j.scheduled_at);
                return (
                  <li key={j.id} className="flex items-start gap-2 text-sm">
                    <span className="text-gray-400 w-12 shrink-0">{format(t, 'h:mm a')}</span>
                    <div>
                      <p className="font-medium text-gray-800">{j.client_name}</p>
                      <p className="text-gray-500 text-xs">{j.tier || j.service_type}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Upcoming jobs panel */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <Calendar size={16} className="text-[#68caf1]" /> Upcoming Jobs (Next 7 Days)
          </h3>
          {stats.upcomingJobs.length === 0 ? (
            <p className="text-gray-400 text-sm">No jobs scheduled in the next 7 days.</p>
          ) : (
            <ul className="space-y-2">
              {stats.upcomingJobs.slice(0, 6).map(j => {
                const t = j.scheduled_at?.toDate ? j.scheduled_at.toDate() : new Date(j.scheduled_at);
                return (
                  <li key={j.id} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#68caf1] shrink-0" />
                      <div>
                        <Link to={`/admin/clients/${j.clientId}`} className="font-medium text-gray-800 hover:text-[#C9A84C]">
                          {j.client_name}
                        </Link>
                        <p className="text-xs text-gray-400">{j.tier || j.service_type}</p>
                      </div>
                    </div>
                    <span className="text-xs text-gray-500 text-right">
                      {format(t, 'EEE, MMM d')}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Lapsed clients */}
        <div className="bg-white rounded-2xl p-5 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <AlertCircle size={16} className="text-amber-500" /> Lapsed Clients (60+ days)
          </h3>
          {stats.lapsedClients.length === 0 ? (
            <p className="text-gray-400 text-sm">No lapsed clients.</p>
          ) : (
            <ul className="space-y-2">
              {stats.lapsedClients.slice(0, 6).map(c => (
                <li key={c.id} className="flex items-center justify-between text-sm">
                  <Link to={`/admin/clients/${c.id}`} className="text-gray-800 hover:text-[#C9A84C] font-medium">
                    {c.name}
                  </Link>
                  <span className="text-xs text-gray-400">
                    Last: {c.lastServiceDate?.toDate
                      ? format(c.lastServiceDate.toDate(), 'MMM d')
                      : c.lastServiceDate}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Upcoming Job Addresses */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <MapPin size={16} className="text-[#68caf1]" /> Job Addresses — Next 14 Days
        </h3>
        <JobAddressList bookings={bookings} />
      </div>

      {/* Test Booking Flows — internal only */}
      <div className="rounded-2xl p-5 border-2 border-dashed border-amber-200 bg-amber-50">
        <h3 className="font-semibold text-amber-800 mb-1 flex items-center gap-2 text-sm">
          🧪 Test Booking Flows
        </h3>
        <p className="text-xs text-amber-600 mb-4">$1 charges · $0.50 deposit · Use real card in Stripe · Hidden from public</p>
        <div className="flex flex-wrap gap-3">
          <a
            href="/detail/book?package=dev_test"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: '#68caf1', color: '#000' }}
          >
            Test One-Time Booking ($1)
          </a>
          <a
            href="/maintenance/book?test=1"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold"
            style={{ background: '#C9A84C', color: '#fff' }}
          >
            Test Maintenance Booking ($1)
          </a>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-2xl p-5 shadow-sm">
        <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Activity size={16} className="text-[#68caf1]" /> Recent Activity
        </h3>
        {activity.length === 0 ? (
          <p className="text-gray-400 text-sm">No activity yet.</p>
        ) : (
          <ul className="grid sm:grid-cols-2 gap-2">
            {activity.map(a => (
              <li key={a.id} className="text-sm flex gap-2 items-start">
                <span className="w-2 h-2 rounded-full bg-[#C9A84C] mt-1.5 shrink-0" />
                <div>
                  <p className="text-gray-700">{a.note}</p>
                  <p className="text-xs text-gray-400">
                    {a.created_at ? format(new Date(a.created_at), 'MMM d, h:mm a') : ''}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
